import { getUrlParamValue } from './elementValueSource';

const snakeToCamel = (s: string) =>
  s.replace(/_([a-z])/gi, (_, c: string) => c.toUpperCase()).replace(/^[A-Z]/, (c) => c.toLowerCase());

function isPresent(v: unknown): boolean {
  return v !== undefined && v !== null && !(typeof v === 'string' && v.trim() === '' && v !== '0');
}

function safeString(v: unknown): string | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v === 'object') return undefined;
  return String(v);
}

/**
 * Expand form variable names to lookup candidates.
 * Forms may use `{card pocket address}` while socket/DB expose `pocket_address` / `card.pocket_address`.
 */
export function expandVariableKeyCandidates(key: string): string[] {
  const trimmed = key.trim();
  if (!trimmed) return [];
  const out: string[] = [];
  const add = (k: string) => {
    const v = k.trim();
    if (v && !out.includes(v)) out.push(v);
  };

  add(trimmed);
  add(trimmed.replace(/\s+/g, '_'));
  add(trimmed.replace(/\s+/g, '.'));
  add(trimmed.replace(/_/g, '.'));
  add(trimmed.replace(/\./g, '_'));
  add(trimmed.replace(/[\s.]+/g, '_'));
  add(trimmed.replace(/[\s_]+/g, '.'));

  // card-prefixed: "card pocket address" | "card.pocket_address" | "card_pocket_address"
  const cardMatch = trimmed.match(/^card(?:[\s._]+)(.+)$/i);
  if (cardMatch) {
    const rest = cardMatch[1].trim();
    add(`card.${rest.replace(/\s+/g, '_')}`);
    add(`card.${rest.replace(/[\s.]+/g, '_')}`);
    add(`card.${rest.replace(/\s+/g, '.')}`);
    add(`card_${rest.replace(/[\s.]+/g, '_')}`);
    // Socket flat card fields
    add(rest);
    add(rest.replace(/\s+/g, '_'));
    add(rest.replace(/[\s.]+/g, '_'));
    add(rest.replace(/\s+/g, '.'));
  }

  return out;
}

/** Resolve a top-level key (exact, case-insensitive, snake/camel). */
function getRawTopLevelValue(data: Record<string, any> | null | undefined, key: string): unknown {
  if (!data) return undefined;
  const k = key.trim();
  if (!k) return undefined;
  if (Object.prototype.hasOwnProperty.call(data, k)) return data[k];
  const dataKeys = Object.keys(data);
  const lower = k.toLowerCase();
  const found = dataKeys.find((kk) => kk.toLowerCase() === lower);
  if (found != null) return data[found];
  const camel = snakeToCamel(k);
  if (Object.prototype.hasOwnProperty.call(data, camel)) return data[camel];
  const foundCamel = dataKeys.find((kk) => snakeToCamel(kk) === camel);
  return foundCamel != null ? data[foundCamel] : undefined;
}

function descendIntoValue(value: unknown, key: string): unknown {
  let obj: any = value;
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch {
      return undefined;
    }
  }
  if (obj == null || typeof obj !== 'object') return undefined;
  if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  const lower = key.toLowerCase();
  const found = Object.keys(obj).find((kk) => kk.toLowerCase() === lower);
  return found != null ? obj[found] : undefined;
}

function resolveOneKey(
  transactionData: Record<string, any>,
  trimmed: string,
): string | undefined {
  // Prefer exact top-level key first (DB returns card.iban as a literal key).
  const exact = safeString(getRawTopLevelValue(transactionData, trimmed));
  if (exact !== undefined) return exact;

  const segments = trimmed.split('.').map((s) => s.trim()).filter(Boolean);
  if (segments.length <= 1) return undefined;

  // Nested object path
  let current: unknown = getRawTopLevelValue(transactionData, segments[0]);
  for (let i = 1; i < segments.length; i++) {
    if (current == null || typeof current !== 'object') {
      current = undefined;
      break;
    }
    current = descendIntoValue(current, segments[i]);
  }
  const nested = safeString(current);
  if (nested !== undefined) return nested;

  // Socket payloads expose card fields flat (iban, pocket_address, …) while forms
  // often reference them as card.iban / card.pocket_address.
  if (segments[0].toLowerCase() === 'card') {
    const flatKey = segments.slice(1).join('.');
    const flat = safeString(getRawTopLevelValue(transactionData, flatKey));
    if (flat !== undefined) return flat;
    const flatUnderscore = safeString(
      getRawTopLevelValue(transactionData, segments.slice(1).join('_')),
    );
    if (flatUnderscore !== undefined) return flatUnderscore;
  }

  return undefined;
}

/**
 * Resolve a variable from transaction-like data.
 * Supports:
 * - literal dotted keys from by-hash-id (`card.iban` as a single key)
 * - spaced card names from the builder (`card pocket address` → `card.pocket_address` / `pocket_address`)
 * - nested paths (`additional_data.tax_id`)
 * - socket flat aliases (`card.iban` → top-level `iban`)
 */
export function getVariableValue(
  transactionData: Record<string, any> | null | undefined,
  variableKey: string,
): string | undefined {
  if (!transactionData || !variableKey || typeof variableKey !== 'string') return undefined;
  const trimmed = variableKey.trim();
  if (!trimmed) return undefined;

  for (const candidate of expandVariableKeyCandidates(trimmed)) {
    const value = resolveOneKey(transactionData, candidate);
    if (value !== undefined) return value;
  }

  return undefined;
}

export type TransactionValueSources = {
  /** Prefer non-null socket fields first. */
  socket?: Record<string, any> | null;
  /** HTTP by-hash-id / DB row. */
  db?: Record<string, any> | null;
  /** When true, also try URL query param with the same key (and flat card.* key). */
  urlFallback?: boolean;
};

/**
 * Resolve with priority: socket (non-null) → db (non-null) → URL query param.
 */
export function resolveTransactionVariable(
  sources: TransactionValueSources,
  variableKey: string,
): string | undefined {
  const fromSocket = getVariableValue(sources.socket ?? null, variableKey);
  if (fromSocket !== undefined && fromSocket !== '') return fromSocket;

  const fromDb = getVariableValue(sources.db ?? null, variableKey);
  if (fromDb !== undefined && fromDb !== '') return fromDb;

  // If socket/db had explicit empty string, still allow URL fallback when enabled.
  if (sources.urlFallback) {
    for (const candidate of expandVariableKeyCandidates(variableKey)) {
      const params = new URLSearchParams(
        typeof window !== 'undefined' ? window.location.search : '',
      );
      if (!params.has(candidate)) continue;
      const fromUrl = getUrlParamValue(candidate);
      if (fromUrl !== '') return fromUrl;
      return fromUrl;
    }
  }

  return fromSocket ?? fromDb;
}

/** Merge socket payload over existing data; null/undefined socket values do not wipe existing DB values. */
export function mergeTransactionData(
  base: Record<string, any> | null | undefined,
  overlay: Record<string, any> | null | undefined,
): Record<string, any> {
  const out: Record<string, any> = { ...(base || {}) };
  if (!overlay || typeof overlay !== 'object') return out;
  for (const [key, value] of Object.entries(overlay)) {
    if (value === null || value === undefined) {
      // Keep existing DB/URL-backed value; only set null if key was missing.
      if (!(key in out)) out[key] = value;
      continue;
    }
    out[key] = value;
  }
  return out;
}

/** True when a value is usable for display (non-null). */
export function hasDisplayValue(v: unknown): boolean {
  return isPresent(v);
}
